import { useMemo, type ReactNode } from 'react';

import { menu } from '@/consts/navigation';
import { buildResults, getFilteredChildren } from '@/features/header/utils/search';
import { normalizeString } from '@/utils/string';
import { CompassOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface SearchResult {
  to: string;
  category: string;
  label: string;
  icon: ReactNode;
}

export const useSearchResults = (query: string): SearchResult[] => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const lowerQuery = normalizeString(query.trim());
    if (!lowerQuery) return [];

    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escapeRegex(lowerQuery)}\\w*`, 'i');

    return menu.flatMap((menuItem) => {
      if (!menuItem.children) return [];

      const menuItemLabel = t(menuItem.text);
      const menuItemIcon = menuItem.icon ?? <CompassOutlined />;

      if (regex.test(normalizeString(menuItemLabel))) {
        return buildResults(menuItemLabel, menuItemIcon, menuItem.children);
      }

      return buildResults(menuItemLabel, menuItemIcon, getFilteredChildren(menuItem, regex));
    });
  }, [query, i18n.language]);
};
